<script lang="ts" setup>
import { computed } from 'vue';
import type { PropType } from 'vue';

import type { MarketModel } from '@exchange/libs/market/service/src';
import { MarketType } from '@exchange/libs/market/service/src/lib/market-model';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';

const props = defineProps({
  market: { type: Object as PropType<MarketModel>, required: true },
});

const baseCode = computed(() => props.market.base.code);
const quoteCode = computed(() => props.market.quote.code);
const lastPrice = computed(() => props.market.lastPrice);
const priceChangePercentage = computed(() => props.market.priceChangePercentageToDisplay);
const marketTitle = computed(() => props.market.name);

const percentagePrecision = CONSTANTS.PRECISION.DEFAULT_PERCENTAGE;
</script>

<template>
  <div class="market-header-mobile">
    <x-logo
      :key="marketTitle"
      class="market-header-mobile__logo"
      :name="baseCode"
      type="crypto"
    />
    <div class="market-header-mobile__name">
      {{ marketTitle }}
      <ot-lozenge
        v-if="market.type === MarketType.PERP"
        :uppercase="false"
        :filled="false"
        :text="$t('fundamentals.perpetual.short')"
        class="market-header-mobile__name__lozenge"
      />
    </div>
    <div class="market-header-mobile__info">
      <ot-number-display
        class="market-header-mobile__price"
        :precision="market.marketPrecision"
        :number="lastPrice"
        :suffix="quoteCode"
        :smaller-suffix="true"
        :grouping="true"
      />
      <ot-number-display
        class="market-header-mobile__change"
        :precision="percentagePrecision"
        :number="priceChangePercentage"
        :add-plus-sign="true"
        :percentage="true"
        :colored-percentage="true"
      />
    </div>
  </div>
</template>

<style lang="scss">
.market-header-mobile {
  display: flex;
  align-items: center;
  gap: 8px;

  &__name {
    display: flex;
    align-items: center;
    color: rgb(var(--v-theme-text-primary));
    font-size: var(--font-size-lg);

    &__lozenge {
      margin-left: 4px;
    }
  }

  &__logo {
    width: 34px;
    height: 34px;
  }

  &__info {
    display: flex;
    flex: 1;
    flex-direction: column;
    color: rgb(var(--v-theme-text-primary));

    --number-display-font-weight: var(--font-weight-medium);
  }

  &__price {
    font-size: var(--font-size-lg);
  }

  &__change {
    font-size: var(--font-size-xs);
  }
}
</style>
